import React, { useState } from "react";
import {
  Badge,
  MultiSelect,
  Paper,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconChevronDown, IconHelpCircle } from "@tabler/icons-react";
import EmailLoading from "../EmailLoading/EmailLoading";
import "./EmailPreview.scss";
import { aiEmailTooltips } from "@/constants.ts";
import { useEmailStore } from "@components/Tax Pro Only/Modals/EmailModal/emailStore.ts";

/**
 * EmailPreview component allows users to preview, edit, and send emails.
 * It integrates with the EmailContext to manage the email content and subject.
 *
 * @returns {JSX.Element} The EmailPreview component.
 */
const EmailPreview: React.FC = () => {
  const [showTagsInput, setShowTagsInput] = useState<boolean>(false);
  const {
    body,
    subject,
    setBody,
    setSubject,
    isGenerating,
    recipients,
    globalRecipients,
    setRecipients,
    source,
  } = useEmailStore();

  let emailInput;

  const globalEmails = globalRecipients.flatMap((r) => r.email);

  if (source === "clientList") {
    if (showTagsInput) {
      emailInput = (
        <MultiSelect
          label="Add email addresses"
          placeholder="Enter your client's email address"
          data={globalEmails}
          defaultValue={globalEmails}
          onChange={(selectedEmails) => {
            setRecipients(selectedEmails.flatMap((e) => ({ email: e })));
          }}
          searchable
          nothingFoundMessage="Client not found..."
          withScrollArea
          className="email-input"
          clearable
          clearButtonProps={{
            "aria-label": "Clear",
          }}
          mt="0.62rem"
          hidePickedOptions
        />
      );
    } else {
      emailInput = (
        <Badge
          color="#0077CC"
          variant="filled"
          className="email-input-badge"
          onClick={() => setShowTagsInput(true)}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            fontSize: "0.85rem",
            padding: "0.85rem",
            textTransform: "none",
            fontWeight: "600",
          }}
          rightSection={<IconChevronDown size={15} />}
        >
          All Clients ({recipients.length})
        </Badge>
      );
    }
  } else {
    const singleEmailValue = recipients.length === 1 ? recipients[0].email : "";
    emailInput = <TextInput value={singleEmailValue} readOnly={true} />;
  }

  return (
    <div className="email-preview-container-parent">
      <Paper>
        <div className="preview-input-label">
          <Text size="md" fw={500}>
            Send To
          </Text>
          <Tooltip label={aiEmailTooltips.sendto}>
            <IconHelpCircle size={15} />
          </Tooltip>
        </div>
        {emailInput}
        <TextInput
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
          data-testid="email-subject-input"
          label={
            <div className="preview-input-label">
              <Text size="md" fw={500} mt="0.62rem">
                Subject
              </Text>
              <Tooltip label={aiEmailTooltips.subject}>
                <IconHelpCircle size={15} />
              </Tooltip>
            </div>
          }
        />
      </Paper>
      <div className="email-preview-container" style={{ position: "relative" }}>
        {isGenerating ? (
          <EmailLoading />
        ) : (
          <Textarea
            label="Message"
            mt="0.62rem"
            placeholder="Edit generated email"
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            className="email-textarea"
            data-testid="email-preview-textarea"
            styles={{
              wrapper: { height: "100%" },
              input: { height: "100%", resize: "none" },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EmailPreview;
